import { useEffect, useMemo, useRef, useState } from "react";
import { FormGenerator, DynamicSourceParameters } from "@proof/ui";
import {
  formProfileDefinition,
  formProfileInputs,
  validateFormProfile,
  type Project,
  type Values,
  type FormProfile,
  type Context,
  type FormProfileCollectionKind,
  type FormProfileLibrary,
} from "@proof/core";
import { Button, Control, Modal } from "./components";
import { useWorkspaceTransport } from "./WorkspaceContext";

export interface FormProfilesProps {
  sourceParameters?: import("@proof/core").SourceParameters;
  onSourceParametersChange?: (
    values: import("@proof/core").SourceParameters,
    forceRefresh: boolean,
  ) => void;
  project: Project;
  instanceId: string;
  versionId?: string;
  onClose: () => void;
  onApply: (values: Values) => void;
}
/** Values remain private to the connection; the server chooses the owner and published definition. */
export function NativeFormProfiles({
  sourceParameters,
  onSourceParametersChange,
  project,
  instanceId,
  versionId,
  onClose,
  onApply,
}: FormProfilesProps) {
  const transport = useWorkspaceTransport(),
    client = transport.client,
    instance = project.formInstances.find((item) => item.id === instanceId)!,
    form = project.forms.find((item) => item.id === instance.formId)!;
  const [showUnmapped, setShowUnmapped] = useState(false);
  const hiddenInputs =
    instance.mappedOnly || instance.profileOnly
      ? form.fields.filter((field) => !instance.mapping[field.id])
      : [];
  const definition = useMemo(() => {
    const p = formProfileDefinition(project, form.id);
    if ((instance.mappedOnly || instance.profileOnly) && !showUnmapped)
      p.fields = p.fields.map((field) =>
        instance.mapping[field.id] ? field : { ...field, formDisplay: "hide" },
      );
    return p;
  }, [project, form.id, instance, showUnmapped]);
  const [collection, setCollection] = useState<FormProfileCollectionKind>(
    (instance.mappedOnly || instance.profileOnly) && form.profileLibrary
      ? "library"
      : "document",
  );
  const scope = {
    sourceParameters,
    ...transport.projectInput(project, versionId),
    formId: form.id,
    collection,
  };
  const available = transport.demo || !!versionId;
  const readOnlyLink = collection === "library" && !!form.profileSource;
  const [listing, setListing] = useState<{
      profiles: FormProfile[];
      definitionHash: string;
      durable: boolean;
      context: Context;
      collection: FormProfileCollectionKind;
      library?: FormProfileLibrary;
      sharingEnabled: boolean;
      sharing: Record<string, string[]>;
      sharedProfiles: import("@proof/core").SharedFormProfileSummary[];
    }>(),
    [values, setValues] = useState<Values>({}),
    [name, setName] = useState(""),
    [selected, setSelected] = useState<FormProfile>(),
    [sharedOwner, setSharedOwner] = useState(""),
    [recipients, setRecipients] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [reviewed, setReviewed] = useState(false),
    createId = useRef(crypto.randomUUID()),
    mounted = useRef(true);
  const scopeKey = JSON.stringify([
    project.id,
    versionId,
    form.id,
    collection,
    sourceParameters,
  ]);
  const operationScope = useRef({ key: scopeKey, client });
  if (
    operationScope.current.key !== scopeKey ||
    operationScope.current.client !== client
  )
    operationScope.current = { key: scopeKey, client };
  const reload = async () => {
    const active = operationScope.current;
    const next = await client.listFormProfiles(scope);
    if (mounted.current && operationScope.current === active) setListing(next);
  };
  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    setListing(undefined);
    setError("");
    setBusy(false);
    if (available)
      void client
        .listFormProfiles(scope, controller.signal)
        .then((next) => {
          if (!controller.signal.aborted) setListing(next);
        })
        .catch((error) => {
          if (!controller.signal.aborted) setError(error.message);
        });
    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, [
    versionId,
    project.id,
    form.id,
    collection,
    client,
    JSON.stringify(sourceParameters),
  ]);
  const incompatible =
    !!selected &&
    selected.definitionHash !== listing?.definitionHash &&
    !reviewed;
  let checked: ReturnType<typeof validateFormProfile> | undefined;
  try {
    checked = validateFormProfile(project, form.id, values, listing?.context);
  } catch {}
  const valid = !!checked && !checked.errors.length;
  const validForApply =
    !!checked &&
    !checked.errors.some(
      (error) =>
        (!instance.mappedOnly && !instance.profileOnly) ||
        !error.fieldId ||
        Object.hasOwn(instance.mapping, error.fieldId),
    );
  const fresh = () => {
    setSelected(undefined);
    setSharedOwner("");
    setRecipients("");
    setValues({});
    setName("");
    setReviewed(false);
    createId.current = crypto.randomUUID();
    setError("");
  };
  useEffect(() => {
    fresh();
  }, [project.id, versionId, form.id, collection, client]);
  const perform = async (action: "create" | "update" | "delete") => {
    if (busy || (sharedOwner && action !== "create")) return;
    const generation = operationScope.current,
      active = () => mounted.current && operationScope.current === generation;
    setBusy(true);
    setError("");
    try {
      if (action === "delete") {
        await client.deleteFormProfile(scope, selected!.id, selected!.revision);
        if (active()) fresh();
      } else {
        const input = {
          name,
          values: formProfileInputs(project, form.id, values, listing?.context),
        };
        const record =
          action === "create"
            ? await client.createFormProfile(scope, {
                ...input,
                id: createId.current,
              })
            : await client.updateFormProfile(scope, selected!.id, {
                ...input,
                expectedRevision: selected!.revision,
              });
        if (active()) {
          setSelected(record);
          setSharedOwner("");
          if (action === "create") setRecipients("");
          setValues(record.values);
          setName(record.name);
          setReviewed(false);
          createId.current = crypto.randomUUID();
        }
      }
      if (active()) await reload();
    } catch (error) {
      if (active()) setError((error as Error).message);
    } finally {
      if (active()) setBusy(false);
    }
  };
  const loadShared = async (owner: string, id: string) => {
    const generation = operationScope.current,
      active = () => mounted.current && operationScope.current === generation;
    setBusy(true);
    setError("");
    try {
      const result = await client.readSharedFormProfile(scope, owner, id);
      if (active()) {
        setSelected(result.profile);
        setSharedOwner(owner);
        setRecipients("");
        setName(result.profile.name);
        setValues(result.profile.values);
        setReviewed(false);
        setListing(
          (current) => current && { ...current, context: result.context },
        );
        createId.current = crypto.randomUUID();
      }
    } catch (error) {
      if (active()) setError((error as Error).message);
    } finally {
      if (active()) setBusy(false);
    }
  };
  const saveSharing = async () => {
    if (!selected || sharedOwner || busy) return;
    const generation = operationScope.current,
      active = () => mounted.current && operationScope.current === generation;
    setBusy(true);
    setError("");
    try {
      const result = await client.shareFormProfile(scope, selected.id, {
        expectedRevision: selected.revision,
        recipients: recipients
          .split(/\r?\n/)
          .map((value) => value.trim())
          .filter(Boolean),
      });
      if (active()) {
        setSelected(result.profile);
        setRecipients(result.recipients.join("\n"));
        await reload();
      }
    } catch (error) {
      if (active()) setError((error as Error).message);
    } finally {
      if (active()) setBusy(false);
    }
  };
  const apply = async () => {
    if (!sharedOwner || !selected) {
      if (checked) onApply(checked.snapshot);
      return;
    }
    const generation = operationScope.current,
      active = () => mounted.current && operationScope.current === generation;
    setBusy(true);
    setError("");
    try {
      const current = await client.readSharedFormProfile(
        scope,
        sharedOwner,
        selected.id,
      );
      if (!active()) return;
      if (
        current.profile.revision !== selected.revision ||
        current.profile.definitionHash !== selected.definitionHash
      )
        throw new Error(
          "The shared profile changed. Reload profiles and select it again.",
        );
      const validated = validateFormProfile(
        project,
        form.id,
        formProfileInputs(project, form.id, values, current.context),
        current.context,
      );
      if (
        validated.errors.some(
          (error) =>
            (!instance.mappedOnly && !instance.profileOnly) ||
            !error.fieldId ||
            Object.hasOwn(instance.mapping, error.fieldId),
        )
      )
        throw new Error(
          "Review the current form values before applying this profile.",
        );
      onApply(validated.snapshot);
    } catch (error) {
      if (active()) setError((error as Error).message);
    } finally {
      if (active()) setBusy(false);
    }
  };
  const sharedKey = (owner: string, id: string) =>
    `shared:${JSON.stringify([owner, id])}`;
  return (
    <Modal title={instance.label} onClose={onClose}>
      {!available ? (
        <p role="status">
          Select a published document version to use personal form profiles.
        </p>
      ) : (
        <>
          {onSourceParametersChange && (
            <DynamicSourceParameters
              project={definition}
              values={sourceParameters}
              onChange={onSourceParametersChange}
            />
          )}
          <p className="muted">
            {transport.demo
              ? "Profiles use the shared demo account on this API. "
              : "Profiles belong to your account. Sharing with other accounts is explicit. "}
            Each instance uses its own variable mapping.
          </p>
          {(form.profileLibrary || collection === "library") && (
            <Control
              label="Profile collection"
              note="Switching collections starts a new profile in the editor."
            >
              <select
                value={collection}
                disabled={busy}
                onChange={(event) => {
                  if (event.target.value === collection) return;
                  fresh();
                  setListing(undefined);
                  setCollection(
                    event.target.value as FormProfileCollectionKind,
                  );
                }}
              >
                <option value="document">This document</option>
                <option value="library" disabled={!listing?.library}>
                  Reusable form across documents
                </option>
              </select>
            </Control>
          )}
          {listing?.library && collection === "library" && (
            <p role="status">
              Profiles for {listing.library.name} are available in other
              documents using this library form.{" "}
              {transport.demo
                ? "They use the shared demo account."
                : "They remain private unless you share them."}
            </p>
          )}
          {form.profileLibrary && listing && !listing.library && (
            <p role="status">
              This form differs from its retained library definition or the
              source is unavailable. Use document profiles, or apply the saved
              library form again to use profiles across documents.
            </p>
          )}
          {listing && !listing.durable && (
            <p role="status">
              This server uses memory storage. Profiles last until the server
              restarts.
            </p>
          )}
          <Control label="Saved form profile">
            <select
              value={
                selected
                  ? sharedOwner
                    ? sharedKey(sharedOwner, selected.id)
                    : selected.id
                  : ""
              }
              disabled={busy || !listing}
              onChange={(event) => {
                const shared = listing?.sharedProfiles?.find(
                  (item) =>
                    sharedKey(item.owner, item.id) === event.target.value,
                );
                if (shared) {
                  void loadShared(shared.owner, shared.id);
                  return;
                }
                const record = listing?.profiles.find(
                  (item) => item.id === event.target.value,
                );
                if (!record) {
                  fresh();
                  return;
                }
                setSelected(record);
                setSharedOwner("");
                setRecipients((listing?.sharing?.[record.id] || []).join("\n"));
                setValues(structuredClone(record.values));
                setName(record.name);
                setReviewed(false);
                setError("");
              }}
            >
              <option value="">New profile</option>
              {listing?.profiles.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
              {!!listing?.sharedProfiles?.length && (
                <optgroup label="Shared with you">
                  {listing!.sharedProfiles.map((item) => (
                    <option
                      key={sharedKey(item.owner, item.id)}
                      value={sharedKey(item.owner, item.id)}
                    >
                      {item.name} · {item.owner}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </Control>
          {sharedOwner && (
            <p role="status">
              Shared by {sharedOwner}. Save a new profile to keep a personal
              copy.
            </p>
          )}
          <fieldset disabled={busy || !listing}>
            <Button onClick={fresh}>Start a new profile</Button>
            {!!hiddenInputs.length && (
              <label>
                <input
                  type="checkbox"
                  checked={showUnmapped}
                  onChange={(event) => setShowUnmapped(event.target.checked)}
                />{" "}
                Show {hiddenInputs.length} unmapped form inputs
              </label>
            )}
            {form.profileSource && collection === "library" && (
              <p>
                Profile values are mapped from the linked collection. Edit saved
                profiles through their original form or Your profiles.
              </p>
            )}
            <FormGenerator
              project={definition}
              values={values}
              onChange={setValues}
              context={listing?.context}
              loadAsset={transport.loadAsset}
              transformImage={transport.proofServices.transformImage}
              upload={transport.upload}
              compact
            />
            {incompatible && (
              <div role="alert">
                <p>
                  This profile was saved with a different form definition.
                  Review its values against the selected version before applying
                  or updating it.
                </p>
                <Button
                  onClick={() => {
                    setValues(
                      formProfileInputs(
                        project,
                        form.id,
                        values,
                        listing?.context,
                      ),
                    );
                    setReviewed(true);
                  }}
                >
                  Review with current form
                </Button>
              </div>
            )}
            {form.allowCreate && (
              <>
                <Control label="Form profile name">
                  <input
                    value={name}
                    maxLength={100}
                    onChange={(event) => setName(event.target.value)}
                  />
                </Control>
                <div className="button-row">
                  <Button
                    disabled={!name.trim() || !valid || incompatible}
                    onClick={() => void perform("create")}
                  >
                    Save new profile
                  </Button>
                  <Button
                    disabled={
                      !selected ||
                      !!sharedOwner ||
                      !name.trim() ||
                      !valid ||
                      incompatible
                    }
                    onClick={() => void perform("update")}
                  >
                    Update selected profile
                  </Button>
                </div>
              </>
            )}
            <div className="button-row">
              <Button
                kind="primary"
                disabled={!validForApply || incompatible}
                onClick={() => void apply()}
              >
                Apply to this instance
              </Button>
              {!readOnlyLink && (
                <Button
                  disabled={!selected || !!sharedOwner}
                  onClick={() => void perform("delete")}
                >
                  Delete selected profile
                </Button>
              )}
            </div>
            {!readOnlyLink &&
              listing?.sharingEnabled &&
              selected &&
              !sharedOwner && (
                <details>
                  <summary>Profile sharing</summary>
                  <Control
                    label="Recipient account IDs (one per line)"
                    note="Only listed accounts can load this profile. Remove all IDs to make it private again."
                  >
                    <textarea
                      value={recipients}
                      onChange={(event) => setRecipients(event.target.value)}
                      rows={3}
                    />
                  </Control>
                  <Button
                    disabled={!form.allowCreate && !!recipients.trim()}
                    onClick={() => void saveSharing()}
                  >
                    Save sharing
                  </Button>
                  <p>
                    {listing.sharing[selected.id]?.length || 0} accounts can
                    load this profile.
                  </p>
                </details>
              )}
          </fieldset>
          {checked?.errors
            .filter(
              (item) =>
                !readOnlyLink ||
                (!instance.mappedOnly && !instance.profileOnly) ||
                !item.fieldId ||
                Object.hasOwn(instance.mapping, item.fieldId),
            )
            .map((item) => (
              <p role="alert" key={`${item.fieldId}:${item.code}`}>
                {item.message}
              </p>
            ))}
          {error && (
            <p role="alert">
              {error}. Your edits have been kept. Reload profiles to review a
              conflict.
            </p>
          )}
          <Button
            disabled={busy}
            onClick={() => {
              const generation = operationScope.current;
              setBusy(true);
              setError("");
              void reload()
                .catch((error) => {
                  if (mounted.current && operationScope.current === generation)
                    setError(error.message);
                })
                .finally(() => {
                  if (mounted.current && operationScope.current === generation)
                    setBusy(false);
                });
            }}
          >
            Reload profiles
          </Button>
          {busy && <p role="status">Updating profiles…</p>}
          {!listing && !error && !busy && (
            <p role="status">Loading profiles…</p>
          )}
        </>
      )}
      {!versionId && error && <p role="alert">{error}</p>}
    </Modal>
  );
}
